import React, { useCallback, useState } from 'react';
import { TiTickOutline } from 'react-icons/ti';
import { useForm } from '~/hooks';
import { CheckBox, FormSelect } from '~/components/form';
import { useAuth } from '~/hooks';
import { lawfirm } from '~/api';

import { useSectionLoader, useAutoSaveMultiple } from '../../hooks';
import sectionTypes from '../sectionTypes';
import { SectionHeader } from '../../../PolicySearchRequest/common';

import PropertyInfoReview from '../PropertyInfo/Review';
import TaxesReview from '../Taxes/Review';
import RestrictiveCovenantsReview from '../RestrictiveCovenants/Review';
import RecordedPlatReview from '../Recorded Plat/Review';
import LienReview from '../Liens/Review';
import EasementsReview from '../Easements/Review';
import AdditionalInfoReview from '../AdditionalInformation/Review';
import { ROLES } from '~/util/auth';
import DeedOfTrustReview from '../DeedOfTust/Review';

/**
 * @typedef ReviewValues
 * @property {number} sectionId
 * @typedef FormState
 * @property {ReviewValues} values The current form values
 */

/** @type {ReviewValues} */
const defaultState = {
    sectionId: 0,
    orderNumber: null,
    attorneyUser: false,
    errorSections: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 7: false, 8: false }
};

const loadData = async (values, user) => {
    const lawFirmUsers = (await lawfirm.getUsersForFirm(user.firmId))
        .filter(x => x.roleId === ROLES.FIRM_ADMIN)
        .map(f => ({
            label: f.firstName + ' ' + f.lastName, value: f.id
        }));

    return {
        ...values,
        user,
        lawFirmUsers
    };
};

const Review = ({ titleOpinionId, readOnly }) => {
    const { user } = useAuth();
    const [isAttorneyChange, setNewAttorney] = useState(false);

    const { onLoad: loadSection } = useSectionLoader(sectionTypes.REVIEW_PTO, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(values, user).then(values => loadSection(values));
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {ReviewValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const { saveMulitpleField } = useAutoSaveMultiple(sectionTypes.REVIEW_PTO, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);

    const validationResult = (sectionType, value) => {
        if (isAttorneyChange) return;

        for (const key of Object.keys(state.values.errorSections)) {
            if (key == sectionType) {
                state.values.errorSections[key] = value;
                break;
            }
        }

        if (state.values.sectionId !== 0 && !readOnly) {
            const data = {
                //attorney: state.values.attorney,
                //attorneyUser: state.values.attorneyUser,
                errorSections: state.values.errorSections
            };
            saveMulitpleField(data, null, state.values.sectionId);
        }
    };

    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><TiTickOutline size="45%" /></div>
                        <div className="ml-2"> Final Review </div>
                    </div>
                </div>
            </div>
            <div className="text-sm mb-2">Please review and confirm the preliminary title opinion information you have provided. Any incomplete information has been identified and must be completed before submitting.</div>
            <SectionHeader colSpan={12}> </SectionHeader>
            <PropertyInfoReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.PROPERTY_INFO, value)} />
            <TaxesReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.TAXES_ASSESMENTS, value)} />
            <RestrictiveCovenantsReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.RESTRICTIVE_COVENANTS, value)} />
            <RecordedPlatReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.RECORDED_PLAT, value)} />
            <LienReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.LIENS, value)} />
            <EasementsReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.EASEMENTS, value)} />
            <DeedOfTrustReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.DEED, value)} />
            <AdditionalInfoReview titleOpinionId={titleOpinionId} validate validationResult={(value) => validationResult(sectionTypes.ADDITIONAL_INFO, value)} />
            <div className="col-span-12 mt-4 text-xs font-semibold">
                This Opinion of title is for the parties to whom it is furnished, is not transferable, and may not be used by any other person or entity without the prior written consent of the undersigned.
            </div>
            {state.values.user && state.values.user.roleId === ROLES.FIRM_ADMIN ?
                <CheckBox
                    label={<span className="text-sm ml-2">{`${state.values.user ? state.values.user.name : ''}`} </span>}
                    path="attorneyUser"
                    required
                    groupClassName="col-span-4 flex items-center text-sm"
                    inputClassName="border-2 border-blue-60 rounded by-white mr-0.5"
                    value={state.values.attorneyUser}
                    readOnly={readOnly}
                    onChange={(attorneyUser) => {
                        setNewAttorney(true);
                        const data = {
                            attorneyUser: attorneyUser,
                            errorSections: state.values.errorSections,
                            attorney: state.values.lawFirmUsers.find(u => u.value == state.values.user.id),
                        };
                        saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
                        return fieldActions.onChange(attorneyUser, 'attorneyUser');
                    }}
                />
                :
                <div className="col-span-4 grid grid-cols-12 grid-flow-row gap-x-2 gap-y-4 text-sm mb-4">
                    <span className="col-span-2 flex items-center text-xs mt-4 mb-4">Certified Attorney Name*</span>
                    <FormSelect
                        type='select'
                        required
                        readOnly={readOnly}
                        options={state.values.lawFirmUsers}
                        isClearable={false}
                        groupClassName="col-span-3 flex items-center text-xs mt-4 mb-4"
                        className="w-full border-2 border-blue-60 rounded bg-gray-50"
                        path='attorney'
                        value={state.values.attorney}
                        error={state.errors.attorney}
                        onChange={(attorney) => {
                            setNewAttorney(true);
                            const data = {
                                attorney: attorney,
                                errorSections: state.values.errorSections
                            };
                            saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
                            return fieldActions.onChange(attorney, 'attorney');
                        }}
                    />
                </div>
            }

            <div className="col-span-12 mt-4 text-xs font-semibold">
                By selecting the &quot;Submit&quot; button, you are signing this &quot;document&quot; electronically. You agree your electronic signature is the legal equivalent of your manual signature on this document. By selecting &quot;Submit&quot;, you consent to be legally bound by this document&apos;s terms and conditions.
            </div>

        </div>
    );
};

export default Review;